import { ChatMessage } from '@/feature/chatbot/types';
import { CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect, useRef } from 'react';
import { MessageBubble } from './MessageBubble';

const MessagesContainer = styled('div')(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  overflow: 'hidden',
}));

const MessagesScrollArea = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: theme.spacing(2, 0),
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const LoadingIndicator = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  padding: theme.spacing(2),
}));

const BlinkingCursor = styled('span')(({ theme }) => ({
  display: 'inline-block',
  width: '1px',
  height: '1em',
  backgroundColor: theme.palette.text.primary,
  marginLeft: '2px',
  animation: 'blink 1s step-end infinite',
  '@keyframes blink': {
    '0%': { opacity: 0.5 },
    '50%': { opacity: 0 },
  },
}));

const StreamingText = styled(Typography)(({ theme }) => ({
  fontSize: '0.875rem',
  lineHeight: 1.5,
  margin: 0,
  display: 'inline',
  fontFamily: theme.typography.fontFamily,
}));

interface GptChatMessagesProps {
  messages: ChatMessage[];
  isLoading?: boolean;
}

export const Messages = ({
  messages,
  isLoading = false,
}: GptChatMessagesProps) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change or when loading state changes
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  return (
    <MessagesContainer>
      <MessagesScrollArea ref={scrollAreaRef}>
        {messages.map((message) => (
          <MessageBubble
            key={message.id}
            isUser={message.sender === 'user'}
            timestamp={message.timestamp}
            content={
              message.isStreaming ? (
                <StreamingText variant="body2">
                  {message.content}
                  <BlinkingCursor />
                </StreamingText>
              ) : (
                message.content
              )
            }
            isReference={message.isReference}
            isStreaming={message.isStreaming}
          />
        ))}
        {isLoading && !messages.some((msg) => msg.isStreaming) && (
          <LoadingIndicator>
            <CircularProgress size={24} />
          </LoadingIndicator>
        )}
      </MessagesScrollArea>
    </MessagesContainer>
  );
};
